<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const search = ref('');
const showDeleteConfirmationPopup = ref(false);
const selected = ref({});

const records = computed(
  () => store.getters['remarketingSequences/getAllSequences']
);
const uiFlags = computed(
  () => store.getters['remarketingSequences/getUIFlags']
);

const filtered = computed(() => {
  if (!search.value) return records.value;
  const q = search.value.toLowerCase();
  return records.value.filter(r => r.name.toLowerCase().includes(q));
});

const formatDuration = seconds => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
};

const tableHeaders = computed(() => [
  t('REMARKETING.LIST.TABLE_HEADER.NAME'),
  t('REMARKETING.LIST.TABLE_HEADER.TRIGGER'),
  t('REMARKETING.LIST.TABLE_HEADER.THRESHOLD'),
  t('REMARKETING.LIST.TABLE_HEADER.STATS'),
  t('REMARKETING.LIST.TABLE_HEADER.STATUS'),
  t('REMARKETING.LIST.TABLE_HEADER.ACTIONS'),
]);

const goToEdit = id => {
  router.push({
    name: 'remarketing_edit',
    params: { accountId: route.params.accountId, id },
  });
};

const goToNew = () => {
  router.push({
    name: 'remarketing_new',
    params: { accountId: route.params.accountId },
  });
};

const goToDashboard = () => {
  router.push({
    name: 'remarketing_dashboard',
    params: { accountId: route.params.accountId },
  });
};

const openDeleteConfirmation = seq => {
  selected.value = seq;
  showDeleteConfirmationPopup.value = true;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('remarketingSequences/delete', selected.value.id);
    useAlert(t('REMARKETING.SAVED'));
  } catch (e) {
    useAlert(t('REMARKETING.ERROR.SAVE_FAILED'));
  } finally {
    showDeleteConfirmationPopup.value = false;
  }
};

const closeDeleteConfirmation = () => {
  showDeleteConfirmationPopup.value = false;
};

onMounted(() => {
  store.dispatch('remarketingSequences/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('REMARKETING.LOADING')"
    :no-records-found="!filtered.length && !search"
    :no-records-message="t('REMARKETING.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('REMARKETING.HEADER')"
        :description="t('REMARKETING.DESCRIPTION')"
        :link-text="t('REMARKETING.LEARN_MORE')"
        feature-name="remarketing"
      >
        <template #actions>
          <Button
            variant="outline"
            icon="i-lucide-bar-chart-3"
            :label="t('REMARKETING.DASHBOARD.TITLE')"
            @click="goToDashboard"
          />
          <Button
            icon="i-lucide-circle-plus"
            :label="t('REMARKETING.HEADER_BTN_TXT')"
            @click="goToNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <div v-if="records.length" class="mb-4">
        <input
          v-model="search"
          type="search"
          :placeholder="t('REMARKETING.SEARCH_PLACEHOLDER')"
          class="block w-full max-w-md px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
        />
      </div>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="th in tableHeaders"
            :key="th"
            class="py-4 ltr:pr-4 rtl:pl-4 rtl:text-right ltr:text-left font-semibold text-n-slate-11"
          >
            {{ th }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="seq in filtered"
            :key="seq.id"
            class="cursor-pointer hover:bg-n-alpha-black2"
            @click="goToEdit(seq.id)"
          >
            <td class="py-4 ltr:pr-4 rtl:pl-4 font-medium text-n-slate-12">
              {{ seq.name }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ t(`REMARKETING.TRIGGER.${seq.trigger_type.toUpperCase()}`) }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{
                seq.inactivity_threshold_seconds
                  ? formatDuration(seq.inactivity_threshold_seconds)
                  : '—'
              }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-xs">
              <span class="text-n-brand">
                {{ seq.stats?.live ?? 0 }} {{ t('REMARKETING.STATS.LIVE') }}
              </span>
              ·
              <span>
                {{ seq.stats?.completed ?? 0 }} {{ t('REMARKETING.STATS.DONE') }}
              </span>
              ·
              <span class="text-n-slate-10">
                {{ seq.stats?.stopped ?? 0 }} {{ t('REMARKETING.STATS.STOPPED') }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="px-2 py-1 text-xs rounded-md"
                :class="
                  seq.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{ seq.active ? t('REMARKETING.ACTIVE') : t('REMARKETING.INACTIVE') }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-right" @click.stop>
              <woot-button
                variant="clear"
                color-scheme="alert"
                size="small"
                icon="dismiss-circle"
                @click="openDeleteConfirmation(seq)"
              >
                {{ t('REMARKETING.DELETE') }}
              </woot-button>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeleteConfirmation"
      :on-confirm="confirmDeletion"
      :title="t('REMARKETING.DELETE')"
      :message="t('REMARKETING.CONFIRM_DELETE')"
      :message-value="` ${selected.name ?? ''}`"
      :confirm-text="t('REMARKETING.DELETE')"
      :reject-text="t('REMARKETING.CANCEL')"
    />
  </SettingsLayout>
</template>
